import { MarkerType } from "@xyflow/react"
import { v4 as uuidv4 } from "uuid"
import {
  WorkflowNode,
  WorkflowEdge,
  ConditionNodeData,
  ActionNodeData,
  BranchCase,
  BranchNodeData,
  TriggerNodeData,
  ExecutionStatus,
  CHECK_KIND_LEAF
} from "lib/types/workflow"
import {
  NodeConditionCheck,
  coerceCheck
} from "lib/types/workflowConditions"
import { apiCheckToExpression } from "./workflowConverter"
import {
  ACTION,
  BRANCH,
  branchCaseHandle,
  CONDITION,
  HANDLE_BRANCH_OTHERWISE,
  HANDLE_INPUT,
  HANDLE_OTHERWISE,
  HANDLE_OUTPUT,
  HANDLE_THEN,
  NODE_TYPE_ACTION,
  NODE_TYPE_BRANCH,
  NODE_TYPE_CONDITION,
  NODE_TYPE_TRIGGER,
  TRIGGER
} from "components/Workflow/nodeKind"

/**
 * Shape returned by GetWorkflowExecution — a trigger node whose `then`
 * string holds the rest of the execution tree as nested nodes.
 */
export interface ExecutionNodeShape {
  nodeId: string
  name: string
  type: string
  completed?: string | null
  failed?: string | null
  failedMessage?: string | null
  matched?: boolean | null
  check?: NodeConditionCheck | string | null
  do?: {
    type: string
    reason?: { code?: string; description?: string }
    customState?: string
    selection?: { giftCardProducts?: boolean }
  } | null
  then?: ExecutionNodeShape[] | string | null
  otherwise?: ExecutionNodeShape[] | string | null
  branches?: ExecutionNodeShape[] | string | null
}

const NODE_X_SPACING = 300
const NODE_Y_SPACING = 180

const parseChildren = (
  value: ExecutionNodeShape[] | string | null | undefined
): ExecutionNodeShape[] => {
  if (!value) return []
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as ExecutionNodeShape[]
    } catch {
      return []
    }
  }
  return value
}

const collectExecutionChildren = (
  node: ExecutionNodeShape
): ExecutionNodeShape[] => {
  const out = [
    ...parseChildren(node.then),
    ...parseChildren(node.otherwise)
  ]
  // For BRANCH nodes, each entry in `branches` is a CONDITION-shaped wrapper
  // carrying its own `then` subtree. The wrappers themselves don't render —
  // we only care about whether their children executed.
  for (const b of parseChildren(node.branches)) {
    out.push(...parseChildren(b.then))
  }
  return out
}

const getStatus = (node: ExecutionNodeShape): ExecutionStatus => {
  if (node.failed) return "failed"
  if (node.completed) return "completed"
  if (node.matched === false) return "skipped"

  const children = collectExecutionChildren(node)
  // BRANCH parents don't carry their own `completed` flag — the backend only
  // marks the matched case's subtree. Derive the parent's status from what
  // actually ran underneath: any failure bubbles up, any success means the
  // branch routed successfully, and otherwise nothing ran below it.
  if (node.type === NODE_TYPE_BRANCH) {
    if (children.some((c) => c.failed)) return "failed"
    if (children.some((c) => c.completed)) return "completed"
    if (children.length > 0) return "skipped"
    return "pending"
  }

  const anyChildRan = children.some((c) => c.completed || c.failed)
  if (!anyChildRan && children.length > 0) {
    // Condition with matched === null means it failed to evaluate.
    if (node.type === NODE_TYPE_CONDITION && node.matched == null) return "failed"
    return "skipped"
  }
  return "pending"
}

const makeEdge = (
  source: string,
  sourceHandle: string,
  target: string
): WorkflowEdge => ({
  id: `e-${source}-${target}`,
  source,
  sourceHandle,
  target,
  targetHandle: HANDLE_INPUT,
  markerEnd: { type: MarkerType.ArrowClosed },
  animated: true
})

const buildActionData = (node: ExecutionNodeShape): ActionNodeData => ({
  nodeType: NODE_TYPE_ACTION,
  label: node.name,
  actionType: node.do?.type ?? "CANCELLATION",
  reason:
    node.do?.type === "CANCELLATION" ? node.do?.reason?.code : undefined,
  reasonDescription:
    node.do?.type === "CANCELLATION"
      ? node.do?.reason?.description
      : undefined,
  customState:
    node.do?.type === "CUSTOM_STATE" ? node.do?.customState : undefined,
  deliveryGiftCardProducts:
    node.do?.type === "DELIVERY"
      ? node.do?.selection?.giftCardProducts
      : undefined
})

const buildConditionData = (node: ExecutionNodeShape): ConditionNodeData => {
  const check = coerceCheck(node.check)
  // If we couldn't parse the check, render an empty PAYMENT_PROVIDER stub
  // so the node still has something the user could edit. Execution view is
  // read-only in practice so this fallback is mostly defensive.
  const expression = check
    ? apiCheckToExpression(check)
    : {
        kind: CHECK_KIND_LEAF,
        check: { type: "PAYMENT_PROVIDER", fields: {} } as const
      }

  return {
    nodeType: NODE_TYPE_CONDITION,
    label: node.name,
    expression
  }
}

const walk = (
  node: ExecutionNodeShape,
  parentId: string,
  sourceHandle: string,
  x: number,
  y: number,
  flowNodes: WorkflowNode[],
  flowEdges: WorkflowEdge[]
): void => {
  const nodeId = node.nodeId
  const status = getStatus(node)
  const failedMessage = node.failed ? node.failedMessage ?? undefined : undefined

  flowEdges.push(makeEdge(parentId, sourceHandle, nodeId))

  if (node.type === NODE_TYPE_ACTION) {
    const data: ActionNodeData = {
      ...buildActionData(node),
      _executionStatus: status,
      _failedMessage: failedMessage
    }
    flowNodes.push({ id: nodeId, type: ACTION, position: { x, y }, data })

    const thenChildren = parseChildren(node.then)
    for (const child of thenChildren) {
      walk(child, nodeId, HANDLE_OUTPUT, x, y + NODE_Y_SPACING, flowNodes, flowEdges)
    }
    return
  }

  if (node.type === NODE_TYPE_CONDITION) {
    const data: ConditionNodeData = {
      ...buildConditionData(node),
      _executionStatus: status,
      _failedMessage: failedMessage
    }
    flowNodes.push({ id: nodeId, type: CONDITION, position: { x, y }, data })

    const thenChildren = parseChildren(node.then)
    for (const child of thenChildren) {
      walk(
        child,
        nodeId,
        HANDLE_THEN,
        x - NODE_X_SPACING,
        y + NODE_Y_SPACING,
        flowNodes,
        flowEdges
      )
    }
    const otherwiseChildren = parseChildren(node.otherwise)
    for (const child of otherwiseChildren) {
      walk(
        child,
        nodeId,
        HANDLE_OTHERWISE,
        x + NODE_X_SPACING,
        y + NODE_Y_SPACING,
        flowNodes,
        flowEdges
      )
    }
    return
  }

  if (node.type === NODE_TYPE_BRANCH) {
    const apiBranches = parseChildren(node.branches)
    const cases: BranchCase[] = apiBranches.map((b) => {
      const check = coerceCheck(b.check)
      const expression = check
        ? apiCheckToExpression(check)
        : {
            kind: CHECK_KIND_LEAF,
            check: { type: "PAYMENT_PROVIDER", fields: {} } as const
          }
      return { id: uuidv4(), label: b.name, expression }
    })

    const data: BranchNodeData = {
      nodeType: NODE_TYPE_BRANCH,
      label: node.name,
      cases,
      _executionStatus: status,
      _failedMessage: failedMessage
    }
    flowNodes.push({ id: nodeId, type: BRANCH, position: { x, y }, data })

    const totalOutcomes = cases.length + 1
    cases.forEach((c, i) => {
      const slotX = x + (i - (totalOutcomes - 1) / 2) * NODE_X_SPACING
      const branchThen = parseChildren(apiBranches[i]?.then)
      for (const child of branchThen) {
        walk(
          child,
          nodeId,
          branchCaseHandle(c.id),
          slotX,
          y + NODE_Y_SPACING,
          flowNodes,
          flowEdges
        )
      }
    })

    const slotX = x + (cases.length - (totalOutcomes - 1) / 2) * NODE_X_SPACING
    for (const child of parseChildren(node.otherwise)) {
      walk(
        child,
        nodeId,
        HANDLE_BRANCH_OTHERWISE,
        slotX,
        y + NODE_Y_SPACING,
        flowNodes,
        flowEdges
      )
    }
  }
}

/**
 * Convert a workflow execution (trigger root + nested then tree) into
 * React Flow nodes and edges, with per-node execution status + failure
 * messages injected so the existing node components can render badges
 * and error blocks.
 */
export const executionToFlow = (
  triggerNode: ExecutionNodeShape,
  workflowName?: string,
  triggerTypeLabel?: string
): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } => {
  // The trigger fires by definition whenever an execution exists, so it
  // always reads as completed unless the backend marked it as failed.
  const triggerStatus: ExecutionStatus = triggerNode.failed ? "failed" : "completed"

  const triggerData: TriggerNodeData = {
    nodeType: NODE_TYPE_TRIGGER,
    label: workflowName ?? triggerNode.name,
    triggerType: triggerTypeLabel ?? triggerNode.type,
    _executionStatus: triggerStatus,
    _failedMessage: triggerNode.failed
      ? triggerNode.failedMessage ?? undefined
      : undefined
  }

  const nodes: WorkflowNode[] = [
    {
      id: triggerNode.nodeId,
      type: TRIGGER,
      position: { x: 250, y: 0 },
      data: triggerData
    }
  ]
  const edges: WorkflowEdge[] = []

  const children = parseChildren(triggerNode.then)
  for (let i = 0; i < children.length; i++) {
    walk(
      children[i],
      triggerNode.nodeId,
      HANDLE_OUTPUT,
      250 + i * NODE_X_SPACING,
      NODE_Y_SPACING,
      nodes,
      edges
    )
  }

  return { nodes, edges }
}
